// A client's billing report: every shift with its hours, rate, travel and total, scoped to a period.
//
// Every amount on this page is the server's (GET /clients/:id/billing-report): an invoiced shift's
// total is what the invoice charged; anything else is the server's preview at the same band rate.
// This page only groups, adds up and labels those figures:
//  - totals are split by what they are (invoiced / delivered but not invoiced / still scheduled),
//    so future bookings never count as billed;
//  - the running total is worked out once over the whole selection, oldest shift first, and then
//    shown newest first, so it means the same thing on every page;
//  - a row whose total isn't hours × rate + travel says why;
//  - no km rate is ever shown unless one is actually set.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import { ChevronDown, ChevronLeft, ChevronRight, Download, Info, Link2, MessageSquare, Printer } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  EmptyState, ErrorState, FilterChips, MoneyStat, OverflowMenu, StatRow, StatusPill, Tooltip,
  fmtDate, fmtTimeRange, humanise, money, num, plural,
} from '../../components/ui';
import { useClientRecord, ClientFrame } from './ClientLayout';
import { dayOf, downloadCsv, fullName, todayIso } from './clientShared';

const PAGE_SIZE = 25;
const SERVER_LIMIT = 200; // the report returns the latest 200 shifts

const cents = (v) => Math.round((Number(v) || 0) * 100);
const fromCents = (c) => c / 100;

// What each row is, for the totals and the status filter.
function kindOf(r) {
  if (r.invoiced) return 'invoiced';
  if (r.status === 'cancelled_with_charge') return 'charged';
  if (r.status === 'cancelled') return 'cancelled';
  if (r.status === 'completed') return 'delivered';
  return 'scheduled';
}
const BILLED = new Set(['invoiced', 'delivered', 'charged']);

const ROW_STATUS = {
  invoiced: { label: 'Invoiced', tone: 'success' },
  delivered: { label: 'Pending invoice', tone: 'info' },
  charged: { label: 'Cancelled (charged)', tone: 'neutral' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

const FILTERS = {
  all: () => true,
  delivered: (k, r) => k === 'delivered' || (k === 'invoiced' && !String(r.status || '').startsWith('cancelled')),
  invoiced: (k) => k === 'invoiced',
  uninvoiced: (k) => k === 'delivered' || k === 'charged',
  scheduled: (k) => k === 'scheduled',
  cancelled: (k, r) => String(r.status || '').startsWith('cancelled'),
};

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function periodRange(period, client, custom) {
  const now = new Date();
  if (period === 'plan') return { from: dayOf(client?.plan_period_start) || null, to: dayOf(client?.plan_period_end) || null };
  if (period === 'month') return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
  if (period === 'last_month') return { from: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), to: iso(new Date(now.getFullYear(), now.getMonth(), 0)) };
  if (period === '90d') return { from: iso(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 89)), to: todayIso() };
  if (period === 'custom') return { from: custom.from || null, to: custom.to || null };
  return { from: null, to: null };
}
const rangeLabel = ({ from, to }) => (from && to ? `${fmtDate(from)} – ${fmtDate(to)}` : from ? `From ${fmtDate(from)}` : to ? `Up to ${fmtDate(to)}` : 'All dates');

// Why a row's total is not hours × rate + travel (only asked when they differ).
function totalNote(r, k, hasKmRate) {
  const notes = [];
  if (k === 'invoiced') notes.push(`Invoice ${r.invoice_number || ''}: the total is what the invoice charged for this shift.`.replace('  ', ' '));
  else if (k === 'charged') notes.push('Short-notice cancellation: the total is the cancellation charge set on the shift, not hours × rate.');
  else if (k === 'cancelled') notes.push('Cancelled without a charge: nothing is billed.');
  if (!r.invoiced && cents(r.travel_cost) > 0 && !hasKmRate) notes.push(`Travel ${money(r.travel_cost)} is estimated at the default rate, because no km rate is set.`);
  return notes.join(' ');
}

// A short explanation that opens on hover or keyboard focus. Wraps, unlike the one-line Tooltip.
function NoteTip({ text, below = false, children }) {
  return (
    <span className="relative inline-flex group/nt">
      {children}
      <span role="tooltip"
        className={clsx('pointer-events-none absolute z-30 w-64 whitespace-normal rounded-lg bg-slate-900 px-2.5 py-1.5 text-left text-[12px] font-normal normal-case leading-snug tracking-normal text-white',
          'opacity-0 transition-opacity duration-100 group-hover/nt:opacity-100 group-focus-within/nt:opacity-100 print:hidden',
          below ? 'right-0 top-full mt-1.5' : 'right-full top-1/2 -translate-y-1/2 mr-2')}>
        {text}
      </span>
    </span>
  );
}

export default function ClientBillingReport() {
  const { id } = useParams();
  const record = useClientRecord(id);
  const { client } = record;

  const [rows, setRows] = useState([]);
  const [state, setState] = useState({ loading: true, error: null });
  const [period, setPeriod] = useState(null);           // null = default (plan period when set)
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [status, setStatus] = useState('all');
  const [daily, setDaily] = useState(false);
  const [page, setPage] = useState(1);
  const [printAll, setPrintAll] = useState(false);

  const load = useCallback(() => {
    setState({ loading: true, error: null });
    api.get(`/clients/${id}/billing-report`)
      .then((r) => { setRows(Array.isArray(r.data) ? r.data : []); setState({ loading: false, error: null }); })
      .catch((e) => setState({ loading: false, error: e.response?.data?.message || 'We couldn’t load the billing report. Check your connection, then try again.' }));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const done = () => setPrintAll(false);
    window.addEventListener('afterprint', done);
    return () => window.removeEventListener('afterprint', done);
  }, []);

  const hasPlan = !!(client && (client.plan_period_start || client.plan_period_end));
  const activePeriod = period || (hasPlan ? 'plan' : 'all');
  const range = periodRange(activePeriod, client, custom);
  useEffect(() => { setPage(1); }, [activePeriod, custom.from, custom.to, status, daily]);

  // Rows in the period, each with what it is.
  const inPeriod = useMemo(() => rows
    .map((r) => ({ ...r, _day: dayOf(r.shift_date), _kind: kindOf(r) }))
    .filter((r) => (!range.from || r._day >= range.from) && (!range.to || r._day <= range.to)),
  [rows, range.from, range.to]);

  // Totals for the period, from the server's figures (added up in cents).
  const totals = useMemo(() => {
    const t = { invoiced: 0, pending: 0, scheduled: 0, charged: 0, chargedCount: 0, hours: 0, scheduledHours: 0, counts: {} };
    for (const r of inPeriod) {
      const k = r._kind;
      t.counts[k] = (t.counts[k] || 0) + 1;
      if (k === 'invoiced') t.invoiced += cents(r.total_cost);
      else if (k === 'delivered') t.pending += cents(r.total_cost);
      else if (k === 'charged') { t.pending += cents(r.total_cost); t.charged += cents(r.total_cost); t.chargedCount += 1; }
      else if (k === 'scheduled') { t.scheduled += cents(r.total_cost); t.scheduledHours += Number(r.hours) || 0; }
      if (k === 'delivered' || (k === 'invoiced' && !String(r.status || '').startsWith('cancelled'))) t.hours += Number(r.hours) || 0;
    }
    return t;
  }, [inPeriod]);

  // The table: filtered, running total worked out oldest first, then shown newest first.
  const shown = useMemo(() => {
    const test = FILTERS[status] || FILTERS.all;
    const list = inPeriod.filter((r) => test(r._kind, r));
    const oldestFirst = [...list].sort((a, b) => a._day.localeCompare(b._day)
      || String(a.start_time || '').localeCompare(String(b.start_time || '')));
    let run = 0;
    const withRun = oldestFirst.map((r) => {
      if (BILLED.has(r._kind)) { run += cents(r.total_cost); return { ...r, _running: run }; }
      return { ...r, _running: null };
    });
    return withRun.reverse();
  }, [inPeriod, status]);

  const showDistance = shown.some((r) => (Number(r.mileage) || 0) > 0);
  const dayTotals = useMemo(() => {
    const m = {};
    for (const r of shown) {
      const d = (m[r._day] = m[r._day] || { hours: 0, total: 0, n: 0 });
      d.hours += Number(r.hours) || 0; d.total += cents(r.total_cost); d.n += 1;
    }
    return m;
  }, [shown]);

  const pages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const safePage = Math.min(page, pages);
  const pageRows = printAll ? shown : shown.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const from = shown.length ? (printAll ? 1 : (safePage - 1) * PAGE_SIZE + 1) : 0;
  const to = printAll ? shown.length : Math.min(safePage * PAGE_SIZE, shown.length);

  // ── Export ────────────────────────────────────────────────────────────────
  const exportCsv = () => {
    if (!shown.length) { toast.error('There are no shifts to export'); return; }
    const head = ['Date', 'Service', 'Support item', 'Worker', 'Start', 'Finish', 'Hours', 'Rate'];
    if (showDistance) head.push('Distance (km)', 'Km rate', 'Travel');
    head.push('Total', 'Total to date', 'Status', 'Invoice');
    const body = shown.map((r) => {
      const kmRate = r.km_rate_configured == null ? '' : Number(r.km_rate_configured).toFixed(2);
      const line = [r._day, humanise(r.shift_name) || '', r.support_item || '', r.staff_name || '',
        String(r.start_time || '').slice(0, 5), String(r.end_time || '').slice(0, 5),
        (Number(r.hours) || 0).toFixed(2), (Number(r.rate) || 0).toFixed(2)];
      if (showDistance) line.push((Number(r.mileage) || 0).toFixed(1), kmRate, (Number(r.travel_cost) || 0).toFixed(2));
      line.push((Number(r.total_cost) || 0).toFixed(2), r._running == null ? '' : fromCents(r._running).toFixed(2),
        (ROW_STATUS[r._kind] || {}).label || humanise(r.status), r.invoice_number || '');
      return line;
    });
    const name = (client ? `${client.first_name || ''}-${client.last_name || ''}` : id).replace(/\s+/g, '-');
    downloadCsv(`billing-${name}-${todayIso()}.csv`, [head, ...body]);
    toast.success(`Exported ${plural(shown.length, 'shift')}`);
  };

  const copyLink = () => {
    const url = window.location.href;
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).then(() => toast.success('Link copied. People need to sign in to open it.')).catch(() => toast.error('Could not copy the link'));
    else toast.error('Copying isn’t available in this browser');
  };

  const print = () => { setPrintAll(true); setTimeout(() => window.print(), 60); };

  const PERIODS = [
    hasPlan && { value: 'plan', label: 'Plan period' },
    { value: 'month', label: 'This month' },
    { value: 'last_month', label: 'Last month' },
    { value: '90d', label: 'Last 90 days' },
    { value: 'all', label: 'All dates' },
    { value: 'custom', label: 'Custom' },
  ].filter(Boolean);

  const chips = [
    { value: 'all', label: 'All', count: inPeriod.length },
    { value: 'delivered', label: 'Delivered', count: inPeriod.filter((r) => FILTERS.delivered(r._kind, r)).length },
    { value: 'invoiced', label: 'Invoiced', count: totals.counts.invoiced || 0 },
    { value: 'uninvoiced', label: 'Not invoiced', count: (totals.counts.delivered || 0) + (totals.counts.charged || 0) },
    { value: 'scheduled', label: 'Scheduled', count: totals.counts.scheduled || 0 },
    { value: 'cancelled', label: 'Cancelled', count: inPeriod.filter((r) => FILTERS.cancelled(r._kind, r)).length },
  ];

  const ExportTrigger = () => (
    <span className="inline-flex items-center gap-1.5 text-sm font-semibold"><Download size={15} aria-hidden />Export<ChevronDown size={14} aria-hidden /></span>
  );

  const TH = 'px-3 py-2.5 text-[12px] font-semibold text-slate-600 whitespace-nowrap bg-white';

  // Rows plus, when asked, a day total after each day's last row.
  const renderRows = () => {
    const out = [];
    const start = printAll ? 0 : (safePage - 1) * PAGE_SIZE;
    pageRows.forEach((r, i) => {
      const k = r._kind;
      const km = Number(r.mileage) || 0;
      const hasKmRate = r.km_rate_configured != null;
      // Compared only to decide whether to explain the total; nothing here is shown as an amount.
      const expected = Math.round((Number(r.hours) || 0) * (Number(r.rate) || 0) * 100) + cents(r.travel_cost);
      const differs = Math.abs(cents(r.total_cost) - expected) > 1 || k === 'charged' || (!r.invoiced && cents(r.travel_cost) > 0 && !hasKmRate);
      const note = differs ? totalNote(r, k, hasKmRate) : '';
      const comment = r.comments || r.notes;
      out.push(
        <tr key={r.id || `${r._day}-${i}`} className="border-b border-slate-100 hover:bg-slate-50/60 break-inside-avoid">
          <td className="px-3 py-2.5 whitespace-nowrap text-slate-700 tabular-nums">{fmtDate(r._day)}</td>
          <td className="px-3 py-2.5 min-w-[11rem]">
            <span className="flex items-start gap-1.5">
              <span className="min-w-0">
                <span className="block text-slate-800">{humanise(r.shift_name) || '—'}</span>
                {r.support_item && <span className="block text-[12px] text-slate-500 tabular-nums">{r.support_item}</span>}
              </span>
              {comment && (
                <Tooltip label={String(comment).slice(0, 120)}>
                  <MessageSquare size={13} className="mt-1 text-slate-400 shrink-0" aria-label={`Comment: ${comment}`} />
                </Tooltip>
              )}
            </span>
          </td>
          <td className="px-3 py-2.5 whitespace-nowrap text-slate-700">{r.staff_name || <span className="text-slate-500">Unassigned</span>}</td>
          <td className="px-3 py-2.5 whitespace-nowrap text-slate-600 tabular-nums">{r.start_time ? fmtTimeRange(r.start_time, r.end_time) : '—'}</td>
          <td className="px-3 py-2.5 text-right whitespace-nowrap text-slate-700 tabular-nums">{num(r.hours, 2)}</td>
          <td className="px-3 py-2.5 text-right whitespace-nowrap text-slate-700 tabular-nums">{money(r.rate)}</td>
          {showDistance && (
            <td className="px-3 py-2.5 text-right whitespace-nowrap text-slate-600 tabular-nums">
              {km > 0 ? <>{num(km, 1)} km <span className="text-slate-500">× {hasKmRate ? money(r.km_rate_configured) : '—'}</span></> : '—'}
            </td>
          )}
          {showDistance && <td className="px-3 py-2.5 text-right whitespace-nowrap text-slate-700 tabular-nums">{cents(r.travel_cost) ? money(r.travel_cost) : '—'}</td>}
          <td className="px-3 py-2.5 text-right whitespace-nowrap font-semibold text-slate-900 tabular-nums">
            <span className="inline-flex items-center justify-end gap-1">
              {note && (
                <NoteTip text={note}>
                  <span tabIndex={0} role="img" aria-label={note} className="inline-flex text-slate-400 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 rounded print:hidden">
                    <Info size={13} aria-hidden />
                  </span>
                </NoteTip>
              )}
              {money(r.total_cost)}
            </span>
            {note && <span className="sr-only print:not-sr-only print:block print:text-[10px] print:font-normal print:text-slate-500">{note}</span>}
          </td>
          <td className="px-3 py-2.5 text-right whitespace-nowrap text-slate-600 tabular-nums">{r._running == null ? '—' : money(fromCents(r._running))}</td>
          <td className="px-3 py-2.5 whitespace-nowrap">
            <StatusPill status={r.status} tone={(ROW_STATUS[k] || {}).tone || 'neutral'} label={(ROW_STATUS[k] || {}).label} className="!text-[11px]" />
            {r.invoice_number && <span className="block text-[12px] text-slate-500 mt-0.5 tabular-nums" title="Invoice number">{r.invoice_number}</span>}
          </td>
        </tr>,
      );
      const after = shown[start + i + 1];
      if (daily && (!after || after._day !== r._day)) {
        const d = dayTotals[r._day];
        out.push(
          <tr key={`${r._day}-day`} className="border-b border-slate-200 bg-slate-50">
            <td colSpan={4} className="px-3 py-2 text-[12.5px] font-semibold text-slate-700">Day total · {fmtDate(r._day)} · {plural(d.n, 'shift')}</td>
            <td className="px-3 py-2 text-right text-[12.5px] font-semibold text-slate-800 tabular-nums">{num(d.hours, 2)}</td>
            <td colSpan={1 + (showDistance ? 2 : 0)} />
            <td className="px-3 py-2 text-right text-[12.5px] font-semibold text-slate-900 tabular-nums">{money(fromCents(d.total))}</td>
            <td colSpan={2} />
          </tr>,
        );
      }
    });
    return out;
  };

  const periodText = rangeLabel(range);

  return (
    <ClientFrame record={record}>
      {/* Printed statement heading (print only) */}
      <div className="hidden print:block">
        <h1 className="text-lg font-semibold text-slate-900">Billing report · {fullName(client)}</h1>
        <p className="text-sm text-slate-600">{periodText}{client?.ndis_number ? ` · NDIS ${client.ndis_number}` : ''} · Printed {fmtDate(todayIso())}</p>
      </div>

      {/* Period, filters and actions */}
      <div className="flex flex-col gap-3 print:hidden">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="billing-period" className="block text-[13px] font-medium text-slate-700 mb-1">Period</label>
            <select id="billing-period" value={activePeriod} onChange={(e) => setPeriod(e.target.value)}
              className="h-9 rounded-lg border border-slate-200 bg-white pl-2.5 pr-8 text-[13px] text-slate-700 hover:border-slate-300 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100">
              {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          {activePeriod === 'custom' && (
            <>
              <div>
                <label htmlFor="billing-from" className="block text-[13px] font-medium text-slate-700 mb-1">From</label>
                <input id="billing-from" type="date" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-[13px]" />
              </div>
              <div>
                <label htmlFor="billing-to" className="block text-[13px] font-medium text-slate-700 mb-1">To</label>
                <input id="billing-to" type="date" value={custom.to} min={custom.from || undefined} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-[13px]" />
              </div>
            </>
          )}
          <p className="pb-2 text-[13px] text-slate-500">{periodText}</p>
          <div className="ml-auto flex items-center gap-2">
            <OverflowMenu label="Export or print the billing report" icon={ExportTrigger}
              className="!px-3 !py-2 border border-slate-300 bg-white !text-slate-700 hover:!bg-slate-50 !rounded-xl"
              items={[
                { label: 'CSV of these shifts', icon: Download, onClick: exportCsv },
                { label: 'Print', icon: Printer, onClick: print },
                { label: 'Copy link', icon: Link2, onClick: copyLink },
              ]} />
          </div>
        </div>
      </div>

      {/* Totals for the period */}
      <section aria-label="Totals for the period" className="bg-white border border-slate-200 rounded-xl px-5 py-4">
        {state.loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[0, 1, 2, 3].map((i) => <div key={i} className="skeleton h-12" />)}</div>
        ) : state.error ? null : (
          <StatRow cols={4}>
            <MoneyStat label="Invoiced" value={fromCents(totals.invoiced)} sub={plural(totals.counts.invoiced || 0, 'shift')} />
            <MoneyStat label="Delivered, not invoiced" value={fromCents(totals.pending)}
              sub={totals.chargedCount ? `Includes ${plural(totals.chargedCount, 'charged cancellation')} (${money(fromCents(totals.charged))})` : plural(totals.counts.delivered || 0, 'shift')} />
            <MoneyStat label="Scheduled, not yet delivered" value={fromCents(totals.scheduled)} tone="muted"
              sub={`${plural(totals.counts.scheduled || 0, 'shift')} · not counted as billed`} />
            <MoneyStat label="Hours delivered" currency={false} value={`${num(totals.hours, 2)} h`}
              sub={totals.scheduledHours ? `${num(totals.scheduledHours, 2)} h scheduled` : 'Delivered and invoiced shifts'} />
          </StatRow>
        )}
        {!state.loading && !state.error && rows.length >= SERVER_LIMIT && (
          <p className="mt-3 text-[12.5px] text-slate-500">This report holds the latest {SERVER_LIMIT} shifts. Older shifts aren’t included in these figures.</p>
        )}
      </section>

      <section aria-label="Shifts" className="bg-white border border-slate-200 rounded-xl print:border-0">
        <div className="flex flex-wrap items-center gap-2 px-3 sm:px-4 py-3 border-b border-slate-100 print:hidden">
          <FilterChips options={chips} value={status} onChange={(v) => setStatus(v || 'all')} multi={false} ariaLabel="Filter shifts by status" />
          <label className="ml-auto inline-flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer select-none">
            <input type="checkbox" checked={daily} onChange={(e) => setDaily(e.target.checked)} className="rounded border-slate-300 accent-primary-600" />
            Show day totals
          </label>
        </div>

        {state.loading ? (
          <div className="p-4 space-y-2.5" aria-busy="true" aria-label="Loading">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-7" />)}</div>
        ) : state.error ? (
          <ErrorState message={state.error} onRetry={load} />
        ) : shown.length === 0 ? (
          <EmptyState title={rows.length ? 'No shifts in this period' : 'No shifts yet'}
            hint={rows.length ? 'Choose another period or status to see more.' : 'Shifts booked for this client appear here with their charges.'} />
        ) : (
          <div className={clsx('overflow-auto overscroll-contain', !printAll && 'max-h-[70vh]', 'print:max-h-none print:overflow-visible')}>
            <table className="w-full border-collapse text-[13px]">
              <caption className="sr-only">Shifts and charges for {periodText}</caption>
              <thead className="sticky top-0 z-10 print:static">
                <tr className="border-b border-slate-200">
                  <th scope="col" className={clsx(TH, 'text-left')}>Date</th>
                  <th scope="col" className={clsx(TH, 'text-left')}>Service</th>
                  <th scope="col" className={clsx(TH, 'text-left')}>Worker</th>
                  <th scope="col" className={clsx(TH, 'text-left')}>Time</th>
                  <th scope="col" className={clsx(TH, 'text-right')}>Hours</th>
                  <th scope="col" className={clsx(TH, 'text-right')}>Rate</th>
                  {showDistance && <th scope="col" className={clsx(TH, 'text-right')}>Distance</th>}
                  {showDistance && <th scope="col" className={clsx(TH, 'text-right')}>Travel</th>}
                  <th scope="col" className={clsx(TH, 'text-right')}>Total</th>
                  <th scope="col" className={clsx(TH, 'text-right')}>
                    <NoteTip text="Delivered, charged and invoiced shifts added up from the start of the period, oldest first. Scheduled shifts aren’t included." below>
                      <span className="inline-flex items-center gap-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400" tabIndex={0}>Total to date <Info size={12} className="text-slate-400 print:hidden" aria-hidden /></span>
                    </NoteTip>
                  </th>
                  <th scope="col" className={clsx(TH, 'text-left')}>Status</th>
                </tr>
              </thead>
              <tbody>{renderRows()}</tbody>
            </table>
          </div>
        )}

        {!state.loading && !state.error && shown.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 px-3 sm:px-4 py-2.5 border-t border-slate-100 print:hidden">
            <span className="text-[12.5px] text-slate-500 tabular-nums">{num(from)}–{num(to)} of {plural(shown.length, 'shift')}</span>
            {pages > 1 && !printAll && (
              <div className="ml-auto flex items-center gap-1">
                <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1} aria-label="Previous page"
                  className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40">
                  <ChevronLeft size={15} aria-hidden />
                </button>
                <span className="px-2 text-[12.5px] text-slate-600 tabular-nums">Page {safePage} of {pages}</span>
                <button type="button" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={safePage === pages} aria-label="Next page"
                  className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40">
                  <ChevronRight size={15} aria-hidden />
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </ClientFrame>
  );
}
